import ApplicationController from "../application_component_controller";
import { useOutlets } from "hb/insights/use_outlets";

export default class extends ApplicationController {
  static targets = ["rail", "tabPanel", "railButton", "tabBody"];
  static values = {
    initialTab: String,
    storageKey: String,
    railWidth: Number,
  };

  connect() {
    useOutlets(this, { components: ["panels"] });

    let active = this._restore();
    if (!active && this.initialTabValue) active = this.initialTabValue;
    if (active && !this._buttonFor(active)) active = null;

    if (active) {
      this._setActive(active);
    } else {
      this._closePanel();
    }

    // The docs tab content is wrapped in <template> until first activation,
    // so its controller (which listens for `doclink`) isn't connected on a
    // fresh page. Bridge the first event here: hydrate by opening the tab,
    // then re-fire so the now-connected docs controller picks it up.
    this.boundBridgeDocLink = this.bridgeDocLink.bind(this);
    window.addEventListener("doclink", this.boundBridgeDocLink);

    // Keep the per-tab width in sync with the user's drags so a refresh
    // restores the dragged size instead of the stale saved value.
    this.boundSyncTabWidth = this.syncTabWidthFromPanel.bind(this);
    // When the user click-snaps the panel via the resizer, treat it the
    // same as clicking the active rail button: clear activeKey + disable
    // the resizer. Without this the rail's state stays out of sync and
    // the next button click is a wasted no-op (the user has to click
    // again to actually reopen).
    this.boundOnPanelsToggle = this.onPanelsToggle.bind(this);
    const panels = this.outletComponent("panels");
    panels?.element.addEventListener("panels:dragend", this.boundSyncTabWidth);
    panels?.element.addEventListener("panels:toggle", this.boundOnPanelsToggle);
  }

  disconnect() {
    if (this.boundBridgeDocLink) {
      window.removeEventListener("doclink", this.boundBridgeDocLink);
    }
    const panels = this.outletComponent("panels");
    if (this.boundSyncTabWidth) {
      panels?.element.removeEventListener("panels:dragend", this.boundSyncTabWidth);
    }
    if (this.boundOnPanelsToggle) {
      panels?.element.removeEventListener("panels:toggle", this.boundOnPanelsToggle);
    }
  }

  onPanelsToggle(event) {
    if (event.detail?.side === "end" && event.detail?.snapped && this.activeKey) {
      this._closePanel();
    }
  }

  syncTabWidthFromPanel() {
    if (!this.activeKey) return;
    const panels = this.outletComponent("panels");
    if (!panels || panels.element.classList.contains("panels-stacked")) return;
    const current = parseInt(
      panels.element.style.getPropertyValue("--end-panel-size"),
      10,
    );
    if (current && current > this.railWidthValue) {
      this._saveTabWidth(this.activeKey, current);
    }
  }

  bridgeDocLink(event) {
    // Already hydrated → the docs controller is listening, nothing to do.
    const docsBody = this.tabBodyTargets.find((b) => b.dataset.key === "docs");
    if (!docsBody || !docsBody.querySelector(":scope > template[data-tab-content]")) {
      return;
    }
    this.openDocs();
    // setTimeout 0 (not microtask) so Stimulus' MutationObserver-driven
    // connect for the freshly-hydrated docs controller has flushed before
    // we redispatch the event.
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("doclink", { detail: event.detail }));
    }, 0);
  }

  toggle(event) {
    const key = event.currentTarget.dataset.key;
    if (this.activeKey === key) {
      this._closePanel();
    } else {
      this._setActive(key);
    }
  }

  // Open the docs tab specifically (used by F1 / programmatic open).
  openDocs() {
    if (this._buttonFor("docs")) this._setActive("docs");
  }

  // Close any open tab (used by programmatic close events).
  closePanel() {
    this._closePanel();
  }

  _setActive(key) {
    const wasActive = this.activeKey;
    this.activeKey = key;

    this.railButtonTargets.forEach((btn) => {
      btn.classList.toggle("is-active", btn.dataset.key === key);
    });
    this.tabBodyTargets.forEach((body) => {
      const shouldShow = body.dataset.key === key;
      body.hidden = !shouldShow;
      if (shouldShow) this._hydrateTab(body);
    });

    // Switching tabs while the panel is already open: keep whatever width
    // the user has (default or dragged). Only resize to the tab's default
    // width when we're opening from a closed/snapped state.
    if (wasActive && wasActive !== key) {
      this.outletComponent("panels")?.element.classList.remove(
        "end-panel-snapped",
      );
    } else {
      const button = this._buttonFor(key);
      const defaultWidth = parseInt(button?.dataset.width, 10) || 700;
      const panelsOpenSize = this.outletComponent("panels")?.endSize;
      const lastOpen = (panelsOpenSize && panelsOpenSize > this.railWidthValue) ? panelsOpenSize : null;
      const tabWidth = this._restoreTabWidth(key) || lastOpen || defaultWidth;
      this._resizePanel(tabWidth, { snapped: false });
    }

    this._setResizerEnabled(true);
    this._save(key);
  }

  _closePanel() {
    // Capture the panel's current open width for this tab so reopen restores
    // it (covers both the default and any width the user dragged to). Skip
    // when stacked — the CSS variable then holds a vertical height that
    // would clobber the saved desktop width.
    const panels = this.outletComponent("panels");
    if (panels && this.activeKey && !panels.element.classList.contains("panels-stacked")) {
      const current = parseInt(
        panels.element.style.getPropertyValue("--end-panel-size"),
        10,
      );
      if (current && current > this.railWidthValue) {
        this._saveTabWidth(this.activeKey, current);
      }
    }

    this.activeKey = null;
    this.railButtonTargets.forEach((btn) => btn.classList.remove("is-active"));
    this.tabBodyTargets.forEach((body) => {
      body.hidden = true;
    });

    this._resizePanel(this.railWidthValue, { snapped: true });
    this._setResizerEnabled(false);
    this._save("");
  }

  _setResizerEnabled(enabled) {
    const panels = this.outletComponent("panels");
    if (!panels) return;
    const resizer = panels.element.querySelector(":scope > .end-resizer");
    if (resizer) resizer.style.pointerEvents = enabled ? "" : "none";
  }

  _resizePanel(size, { snapped }) {
    this.outletComponent("panels")?.applyEndState({ size, snapped });
  }

  _buttonFor(key) {
    return this.railButtonTargets.find((b) => b.dataset.key === key);
  }

  _hydrateTab(body) {
    const template = body.querySelector(":scope > template[data-tab-content]");
    if (!template) return;
    body.appendChild(template.content);
    template.remove();
  }

  _restore() {
    if (!this.storageKeyValue) return null;
    try {
      return sessionStorage.getItem(this.storageKeyValue) || null;
    } catch {
      return null;
    }
  }

  _save(value) {
    if (!this.storageKeyValue) return;
    try {
      if (value) {
        sessionStorage.setItem(this.storageKeyValue, value);
      } else {
        sessionStorage.removeItem(this.storageKeyValue);
      }
    } catch {
      /* ignore */
    }
  }

  _saveTabWidth(key, width) {
    if (!this.storageKeyValue || !key || !width) return;
    try {
      localStorage.setItem(
        `${this.storageKeyValue}:width:${key}`,
        String(width),
      );
    } catch {
      /* ignore */
    }
  }

  _restoreTabWidth(key) {
    if (!this.storageKeyValue || !key) return null;
    try {
      const v = localStorage.getItem(`${this.storageKeyValue}:width:${key}`);
      const n = v ? parseInt(v, 10) : NaN;
      return Number.isFinite(n) ? n : null;
    } catch {
      return null;
    }
  }
}
